import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { RowEvent } from 'ag-grid-community';
import { ButtonClick, ModalDialogNotificationService, NotificationType } from '@se/se.ui.html5';
import { EstensaWaitingService, GridSelectionModel } from '@se/platform';
import { BaseExamination } from '@se/models';
import { firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { RemoveExamService } from './remove-exam.service';

@Component({
    selector: 'remove-exam',
    templateUrl: './remove-exam.component.html',
    styleUrls: ['./remove-exam.component.scss'],
})
export class RemoveExamComponent {

    @Input() selectedExamination: BaseExamination;
    @Input() showDialog: boolean;
    @Output() closeDialog: EventEmitter<boolean> = new EventEmitter<boolean>();

    items: BaseExamination[];
    gridOptions: any;
    loading = false;
    message: string;

    private _exams: BaseExamination[] = [];

    get exams(): BaseExamination[] {
        return this._exams;
    }

    constructor(
        private translate: TranslateService,
        public modalDialogNotificationService: ModalDialogNotificationService,
        private removeExamService: RemoveExamService,
        private waitingService: EstensaWaitingService,
    ) {
        this.gridOptions = {
            rowSelection: GridSelectionModel.multiple,
            suppressCellFocus: true,
            suppressLoadingOverlay: true,
            columnDefs: removeExamService.buildColumns(),
            defaultColDef: { editable: false, sortable: true, resizable: true },
            enableBrowserTooltips: true,
        };
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.showDialog && changes.showDialog.currentValue) {
            this.onOpen();
        }
    }

    onSearch() {
        this.loading = true;
        this.removeExamService.search(this.selectedExamination.idReport || this.selectedExamination.idClinicalReport).subscribe({
            next: (items) => {
                this.loading = false;
                this.items = items.filter((item) => item.id != this.selectedExamination.id);
            },
            error: (exception) => {
                this.loading = false;
                this.message = this.lookupErrorMessage(exception);
                this._exams = [];
                this.items = [];
            },
        });
    }

    onSelectionChange(e: RowEvent) {
        this._exams = e.api.getSelectedRows();
    }

    canRemove(): boolean {
        return this._exams?.length && !this.loading;
    }

    onClose(refreshReportList: boolean) {
        this.loading = false;
        this.message = '';
        this._exams = [];
        this.items = [];
        this.closeDialog.emit(refreshReportList);
    }

    async onRemove() {
        let buttonClick: ButtonClick = await firstValueFrom(this.modalDialogNotificationService
            .notify({
                buttonYes: true,
                buttonNo: true,
                buttonCancel: false,
                buttonOk: false,
                title: this.translate.instant('Warning'),
                message: this.translate.instant('Visit_RemoveExamConfirm_1'),  // Gli esami selezionati verranno rimossi dal referto. Si vuole procedere?
                type: NotificationType.warning,
            }));
        if (buttonClick !== ButtonClick.yes) {
            return;
        }
        this.waitingService.isWaiting = true;
        const idExaminations = this.exams.map(e => e.id);
        this.removeExamService.removeExam(this.selectedExamination.idReport || this.selectedExamination.idClinicalReport, idExaminations).pipe(finalize(() => {
            this.waitingService.isWaiting = false;
            this.onClose(true);
        })).subscribe();
    }

    private lookupErrorMessage(exception) {
        if (exception && exception.error && exception.error.estensaFaultDetails) {
            if (exception.error.estensaFaultDetails.typeName === 'TooMuchResultFault') {
                return this.translate.instant(exception.error.estensaFaultDetails.typeName, {
                    p0: exception.error.estensaFaultDetails.estensaFault.count,
                    p1: exception.error.estensaFaultDetails.estensaFault.limit,
                    p2: ' ',
                });
            }
            return this.translate.instant('AssociateFailed') + ' ' + exception.message;
        }
    }

    private onOpen() {
        if (!this.selectedExamination) {
            return;
        }
        this.onSearch();
    }
}
